import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Empty, Tooltip, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
  AFS_BOND,
  CORPORATE_BOND,
  EQUITY,
  ETFEquities as ETF_EQUITIES,
  ETFCommodities as ETF_COMMODITIES,
  FUTURES,
  FX_FWD,
  GOVT_BOND,
  HTM_BOND,
  MarginOptions,
  selectors,
  updateLeverageSettings,
} from 'state';
import SettingsCard from '../../SessionSettingsShared/components/SettingsCard';
import { isSessionLocked } from '../../SessionSettingsShared/helpers';
import InlineNumber from './InlineNumber';
import './inline_edit.css';

type MarginState = { [type: string]: MarginOptions };
type FuturesState = {
  maintenance: number; // displayed as percent (0-100)
  periods: number; // raw count
  feePct: number; // displayed as percent (0-100)
};

const MARGIN_ROWS: { type: string; label: string }[] = [
  { type: 'FxForwards', label: 'FxForwards' },
  { type: 'Bonds', label: 'Bonds' },
  { type: 'Equity', label: 'Equity' },
  { type: 'ETFEquities', label: 'ETF Equities' },
  { type: 'ETFCommodities', label: 'ETF Commodities' },
  { type: 'Futures', label: 'Futures' },
];

// A margin row is only meaningful when the session's instrument universe
// includes an asset class its *_leverage field actually applies to on the
// backend (is_equity / is_bond / is_fwd / is_futures) — otherwise the value is
// inert. Keyed by the row's payload `type`; Bonds covers every Bond subclass.
const ROW_ASSET_CLASSES: { [type: string]: number[] } = {
  FxForwards: [FX_FWD],
  Bonds: [GOVT_BOND, CORPORATE_BOND, HTM_BOND, AFS_BOND],
  Equity: [EQUITY],
  ETFEquities: [ETF_EQUITIES],
  ETFCommodities: [ETF_COMMODITIES],
  Futures: [FUTURES],
};

const MAINTENANCE_INFO =
  'Maintenance threshold: A futures position is auto-liquidated once its remaining equity (margin + PnL) falls to or below the maintenance percentage of the posted margin. 0% = liquidate only when margin is fully wiped.';
const TERM_INFO =
  'Every N periods the futures contract expires and is automatically rolled to the next contract, charging the rollover fee on each open position. 0 = rollover disabled.';
const FEE_INFO =
  "Charged on each open futures position at every rollover, as a percentage of the position's notional. If a player can't cover the fee, that position is liquidated instead.";

/**
 * Seamless v2 of LeveragePanel. The *_leverage fields store a MARGIN FRACTION
 * (1.0 = fully funded / no leverage). No edit mode: every control is always
 * mounted. Margin cells (a fraction shown as %) and the futures fields commit
 * on blur/Enter, each dispatching the FULL updateLeverageSettings payload the
 * v1 panel used on Save. marginSettingsLocked disables every control.
 */
const LeverageCardV2: React.FC = () => {
  const dispatch = useDispatch();
  const session = useSelector(selectors.activeSession);
  const marginSettingsLocked = isSessionLocked(session);

  // Show only the rows whose asset class the session can actually trade, and
  // hide the whole card / the Futures card when none apply. Same gating source
  // (playableAssetClasses, derived from session.underlying_assets) that the
  // sibling LimitsCardV2 uses.
  const playableAssetClasses_ = useSelector(selectors.playableAssetClasses());
  const visibleRows = MARGIN_ROWS.filter(row =>
    ROW_ASSET_CLASSES[row.type].some(ac => playableAssetClasses_.includes(ac)),
  );
  const showFutures = playableAssetClasses_.includes(FUTURES);

  // The session's persisted margins, in the shape updateLeverageSettings
  // expects. Also used as the (no-op) margins payload of the futures saves so
  // they never clobber the margin table, and to seed local state (no flash).
  const buildSessionMargins = (): MarginState => ({
    FxForwards: { type: 'FxForwards', margin: session.fx_leverage },
    Bonds: { type: 'Bonds', margin: session.bond_leverage },
    Equity: { type: 'Equity', margin: session.equity_leverage },
    ETFEquities: {
      type: 'ETFEquities',
      margin: session.etf_equities_leverage,
    },
    ETFCommodities: {
      type: 'ETFCommodities',
      margin: session.etf_commodities_leverage,
    },
    Futures: { type: 'Futures', margin: session.futures_leverage },
  });

  const [margins, setMargins] = useState<MarginState>(buildSessionMargins);
  const [futures, setFutures] = useState<FuturesState>({
    maintenance: session.futures_maintenance_threshold * 100,
    periods: session.futures_rollover_periods,
    feePct: session.futures_rollover_fee_pct * 100,
  });

  useEffect(() => {
    setMargins(buildSessionMargins());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    session.fx_leverage,
    session.bond_leverage,
    session.equity_leverage,
    session.etf_equities_leverage,
    session.etf_commodities_leverage,
    session.futures_leverage,
  ]);

  useEffect(() => {
    setFutures({
      maintenance: session.futures_maintenance_threshold * 100,
      periods: session.futures_rollover_periods,
      feePct: session.futures_rollover_fee_pct * 100,
    });
  }, [
    session.futures_maintenance_threshold,
    session.futures_rollover_periods,
    session.futures_rollover_fee_pct,
  ]);

  const commitMargin = (type: string, fraction: number) => {
    const next: MarginState = {
      ...margins,
      [type]: { type, margin: fraction },
    };
    setMargins(next);
    dispatch(updateLeverageSettings({ margins: next }));
  };

  // Each futures commit dispatches the full payload using the latest local
  // futures values (the just-committed field overridden in `next`).
  const commitFutures = (next: FuturesState) => {
    setFutures(next);
    dispatch(
      updateLeverageSettings({
        margins: buildSessionMargins(),
        futures_maintenance_threshold: next.maintenance / 100,
        futures_rollover_periods: next.periods,
        futures_rollover_fee_pct: next.feePct / 100,
      }),
    );
  };

  return (
    <>
      <SettingsCard title="Margin requirements" className="set3b-fill-table">
        {visibleRows.length > 0 ? (
          <table className="ie-table">
            <colgroup>
              <col style={{ width: '60%' }} />
              <col style={{ width: '40%' }} />
            </colgroup>
            <thead>
              <tr>
                <th>Type</th>
                <th className="ie-col-num">Percentage</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map(row => {
                const margin = margins[row.type]?.margin ?? null;
                return (
                  <tr key={row.type}>
                    <td className="ie-row-label">{row.label}</td>
                    <td className="ie-col-num">
                      <InlineNumber
                        value={margin}
                        onCommit={v => commitMargin(row.type, v)}
                        disabled={marginSettingsLocked}
                        step={0.01}
                        min={0.01}
                        max={1}
                        formatter={value =>
                          `${Math.round((Number(value) || 0) * 100)}%`
                        }
                        parser={value => {
                          const nr = Number(`${value}`.replaceAll('%', ''));
                          return isNaN(nr) ? 0 : Number((nr / 100).toFixed(2));
                        }}
                        ariaLabel={`${row.label} margin percentage`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <Empty
            description={
              <Typography.Text type="secondary">
                No leverage enabled instruments selected.
              </Typography.Text>
            }
          />
        )}
      </SettingsCard>

      {showFutures && (
        <SettingsCard title="Futures">
          <div className="ie-formgrid">
            <div className="ie-formrow">
              <span className="ie-formrow__label">
                Maintenance
                <Tooltip title={MAINTENANCE_INFO}>
                  <InfoCircleOutlined style={{ marginLeft: 6 }} />
                </Tooltip>
              </span>
              <div className="ie-formrow__control">
                <InlineNumber
                  value={futures.maintenance}
                  onCommit={v => commitFutures({ ...futures, maintenance: v })}
                  disabled={marginSettingsLocked}
                  min={0}
                  max={100}
                  step={1}
                  plain
                  addonAfter="%"
                  ariaLabel="Futures maintenance threshold"
                />
              </div>
            </div>

            <div className="ie-formrow">
              <span className="ie-formrow__label">
                Contracts term
                <Tooltip title={TERM_INFO}>
                  <InfoCircleOutlined style={{ marginLeft: 6 }} />
                </Tooltip>
              </span>
              <div className="ie-formrow__control">
                <InlineNumber
                  value={futures.periods}
                  onCommit={v =>
                    commitFutures({
                      ...futures,
                      periods: Math.max(0, Math.round(v)),
                    })
                  }
                  disabled={marginSettingsLocked}
                  min={0}
                  step={1}
                  plain
                  // Keep the plural grammatically correct but reserve the
                  // trailing "s" width always (visibility:hidden keeps layout
                  // space), so period↔periods never resizes the addon → never
                  // widens the control/column/card → zero layout shift.
                  addonAfter={v => (
                    <>
                      period
                      <span
                        style={{ visibility: v === 1 ? 'hidden' : 'visible' }}
                      >
                        s
                      </span>
                    </>
                  )}
                  ariaLabel="Futures contracts term"
                />
              </div>
            </div>

            <div className="ie-formrow">
              <span className="ie-formrow__label">
                Rollover fee
                <Tooltip title={FEE_INFO}>
                  <InfoCircleOutlined style={{ marginLeft: 6 }} />
                </Tooltip>
              </span>
              <div className="ie-formrow__control">
                <InlineNumber
                  value={futures.feePct}
                  onCommit={v => commitFutures({ ...futures, feePct: v })}
                  disabled={marginSettingsLocked}
                  min={0}
                  max={100}
                  step={0.1}
                  plain
                  addonAfter="%"
                  ariaLabel="Futures rollover fee"
                />
              </div>
            </div>
          </div>
        </SettingsCard>
      )}
    </>
  );
};

export default LeverageCardV2;
